import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NavigationHeader from '../navigation/NavigationHeader';
import AccordionGroup, { type AccordionGroupRef } from '@/components/commos/AccordionGroup';
import { z } from 'zod';
import { hideGlobalModal, showGlobalModal } from '@/stores/modalStore';
import { useAlertStore } from '@/stores/alertStore';
import { useNavigate } from 'react-router-dom';
import { catalogService, type CatalogsResponse } from '@/services/catalogService';

interface PageConfig {
  title: string;
  description: string;
  tablePath: string;
}

interface CreationPageProps<T extends z.ZodType, D> {
  pageConfig: PageConfig;
  saveButton: (isDisabled: boolean, isFetching: boolean) => React.ReactElement;
  items: Array<{
    id: string;
    title: string;
    Component: () => React.ReactNode;
  }>;
  saveFunction: (data: D) => Promise<{ success: boolean; message?: string }>;
  ContextProvider: React.Provider<{
    onClearFile: () => void;
    onUpload: (file: File) => void;
    onComplete: (data: z.infer<T>) => void;
    isFetching: boolean;
    catalogs: CatalogsResponse | null;
  }>;
  modalConfig: {
    title: string;
    message: string;
    successText: string;
    successMessage: (fileName?: string) => string;
    errorText: string;
  };
}

const CreationPage = <T extends z.ZodType, D>({
  pageConfig,
  saveButton,
  items,
  saveFunction,
  ContextProvider,
  modalConfig,
}: CreationPageProps<T, D>) => {
  const { title, description, tablePath } = pageConfig;
  const ref = useRef<AccordionGroupRef | null>(null);
  const fileRef = useRef<File | null>(null);

  const navigate = useNavigate();
  const showAlert = useAlertStore(state => state.showAlert);

  const [isFetching, setIsFetching] = useState(false);
  const [catalogs, setCatalogs] = useState<CatalogsResponse | null>(null);
  const [isDisabled, setIsDisabled] = useState(true);

  const itemsActivated = useMemo(() => {
    if (!fileRef.current) return items;
    return items.map(item => (item.id === 'image' ? { ...item, description: 'Imagen cargada' } : item));
  }, [fileRef.current]);

  const onClearFile = useCallback(() => {
    fileRef.current = null;
    ref.current?.disable();
    setIsDisabled(true);
  }, []);

  const onUpload = useCallback((file: File) => {
    fileRef.current = file;
    ref.current?.next();
    setIsDisabled(false);
  }, []);

  const onComplete = useCallback((data: z.infer<T>) => {
    showGlobalModal({
      title: modalConfig.title,
      message: modalConfig.message,
      iconType: 'warning',
      primaryAction: {
        label: 'Guardar',
        onClick: () => {
          void (async () => {
            setIsFetching(true);
            const result = await saveFunction({
              ...(data as Record<string, any>),
              image: fileRef.current || ''
            } as D);
            if (result.success) {
              showAlert({
                text: modalConfig.successText,
                message: modalConfig.successMessage(fileRef.current?.name),
                status: 'success',
              });
              void navigate(tablePath);
            } else {
              showAlert({
                text: modalConfig.errorText,
                message: result.message,
                status: 'error',
              });
            }
            setIsFetching(false);
          })();
        },
        variant: 'primary',
      },
      secondaryAction: {
        label: 'Cancelar',
        onClick: () => {
          hideGlobalModal();
        },
      },
    });
  }, [saveFunction, modalConfig, tablePath, navigate, showAlert]);

  const contextValue = useMemo(() => ({
    onClearFile,
    onUpload,
    onComplete,
    isFetching,
    catalogs,
  }), [onUpload, onComplete, isFetching, catalogs, onClearFile]);

  useEffect(() => {
    const fetchCatalogs = async () => {
      const catalogs = await catalogService.getCatalogs();
      setCatalogs(catalogs);
    }
    void fetchCatalogs();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <NavigationHeader
        title={title}
        description={description}
        backLink="/"
        actions={[saveButton(isDisabled, isFetching)]}
      />
      <div className="container mx-auto px-4 py-8">
        <ContextProvider value={contextValue}>
          <AccordionGroup items={itemsActivated} ref={ref} />
        </ContextProvider>
      </div>
    </div>
  );
};

export default CreationPage;
